import { queryOptions } from "@tanstack/react-query";
import { queryClient } from "@/shared/api/queryClient";
import { listMappings, listVendorDirectory } from "./bodycamMappingsApi";
import type { VendorDirectoryEntry } from "./bodycamMappingsApi";
import type { ExternalUserMapping } from "@/shared/interfaces/ExternalUserMapping";

export const bodycamMappingKeys = {
  all: ["bodycamMappings"] as const,
  station: (stationId: string) =>
    [...bodycamMappingKeys.all, stationId] as const,
  mappings: (stationId: string, opts: { includeInactive: boolean }) =>
    [...bodycamMappingKeys.station(stationId), "mappings", opts] as const,
  directory: (stationId: string) =>
    [...bodycamMappingKeys.station(stationId), "directory"] as const,
};

export function stationMappingsQuery(stationId: string, includeInactive = false) {
  return queryOptions<ExternalUserMapping[]>({
    queryKey: bodycamMappingKeys.mappings(stationId, { includeInactive }),
    queryFn: () => listMappings(stationId, includeInactive),
    staleTime: 60_000,
  });
}

export function vendorDirectoryQuery(stationId: string) {
  return queryOptions<VendorDirectoryEntry>({
    queryKey: bodycamMappingKeys.directory(stationId),
    queryFn: () => listVendorDirectory(stationId),
    // A roster changes about as often as the agency hires, and each miss is a
    // 10s vendor scan — hold it for the session and refresh on demand.
    staleTime: 15 * 60_000,
    gcTime: 30 * 60_000,
    // The default policy would turn one vendor timeout into three 10s scans;
    // the panel offers an explicit Retry instead.
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Drops everything cached under a station's bodycam binding. Both queries are
 * addressed by station alone — the server resolves the binding — so a binding
 * that is removed or re-pointed to another vendor leaves the old vendor's
 * roster cached, and the directory holds for 15 minutes without refetching on
 * focus. Cancelling first stops an in-flight 10s scan started under the old
 * binding from repopulating the cache after the change.
 */
export async function resetStationBodycamCache(stationId: string) {
  const queryKey = bodycamMappingKeys.station(stationId);
  await queryClient.cancelQueries({ queryKey });
  queryClient.removeQueries({ queryKey });
}
